import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/sonner";

import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import NetworkStatusBanner from "@/components/NetworkStatusBanner";
import { AuthProvider } from "@/context/AuthContext";
import { ShopProvider } from "@/context/ShopContext";
import { QuickBuyProvider } from "@/context/QuickBuyContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { LuxLoader } from "@/components/LuxLoader";
import CookieConsentBanner from "@/components/CookieConsentBanner";

// Lazy-mounted shells: these modals never render on first paint (they wait for
// a user trigger), so we defer their JS with a `null` Suspense fallback. If a
// chunk fails to load the shell simply stays hidden — the rest of the app keeps
// working.
const QuickBuySheet = lazy(() => import("@/components/QuickBuySheet"));
const AgeGateModal = lazy(() => import("@/components/auth/AgeGateModal"));

// Eager (critical path): ONLY the Home page is on the first paint for most
// users. Keeping it eager avoids the tiny Suspense flash on the entry route.
// Every other page moves to its own async chunk to keep main.js small.
import Home from "@/pages/Home";
import NotFound from "@/pages/NotFound";

// Lazy (off critical path): every non-home page becomes its own async chunk,
// including Cart / Wishlist / Search / ProductList which are one-click away
// but not part of the initial render.
const ProductList = lazy(() => import("@/pages/ProductList"));
const Cart = lazy(() => import("@/pages/Cart"));
const Wishlist = lazy(() => import("@/pages/Wishlist"));
const Search = lazy(() => import("@/pages/Search"));
const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const ProductDetails = lazy(() => import("@/pages/ProductDetails"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const Profile = lazy(() => import("@/pages/Profile"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const Collection = lazy(() => import("@/pages/Collection"));
const OrderTracking = lazy(() => import("@/pages/OrderTracking"));
const OrderSuccess = lazy(() => import("@/pages/OrderSuccess"));
const GuestTrackOrder = lazy(() => import("@/pages/GuestTrackOrder"));
const About = lazy(() => import("@/pages/legal/About"));
const Contact = lazy(() => import("@/pages/legal/Contact"));
const Terms = lazy(() => import("@/pages/legal/Terms"));
const Privacy = lazy(() => import("@/pages/legal/Privacy"));
const ReturnPolicy = lazy(() => import("@/pages/legal/ReturnPolicy"));
const Shipping = lazy(() => import("@/pages/legal/Shipping"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));

// Full-page suspense fallback — reuses the app's shared luxury loader so the
// visual language stays identical whether the route is eager or lazy.
const RouteFallback = () => (
  <div className="grid min-h-screen place-items-center bg-white">
    <LuxLoader label="Loading" />
  </div>
);

// Small helper so every lazy route gets the same fallback without repetition.
const L = (el) => <Suspense fallback={<RouteFallback />}>{el}</Suspense>;

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <NetworkStatusBanner />
        <ErrorBoundary>
          <AuthProvider>
            <ShopProvider>
              <NotificationProvider>
                <QuickBuyProvider>
                  <Routes>
                    {/* Auth routes without bottom nav */}
                    <Route path="/login" element={L(<Login />)} />
                    <Route path="/signup" element={L(<Signup />)} />
                    <Route path="/forgot-password" element={L(<ForgotPassword />)} />
                    <Route path="/reset-password" element={L(<ResetPassword />)} />
                    <Route path="/verify-email" element={L(<VerifyEmail />)} />
                    <Route path="/auth/callback" element={L(<AuthCallback />)} />
                    <Route path="/admin/login" element={L(<AdminLogin />)} />

                    {/* Admin console — dedicated layout, no customer bottom nav */}
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute adminOnly>{L(<Admin />)}</ProtectedRoute>
                      }
                    />

                    {/* Main routes with bottom nav */}
                    <Route element={<Layout />}>
                      <Route index element={<Home />} />
                      {/* P0-7 · Deep-link aliases — the sitemap, Play Store
                          screenshots, and legacy links all reference /shop
                          and /orders (no id). Give both a graceful landing. */}
                      <Route path="shop" element={<Navigate to="/" replace />} />
                      <Route path="orders" element={<Navigate to="/profile" replace />} />
                      <Route path="category/:category" element={L(<ProductList />)} />
                      <Route path="collection/:key" element={L(<Collection />)} />
                      <Route path="product/:id" element={<ErrorBoundary>{L(<ProductDetails />)}</ErrorBoundary>} />
                      <Route path="cart" element={L(<Cart />)} />
                      <Route path="wishlist" element={L(<Wishlist />)} />
                      <Route path="search" element={L(<Search />)} />
                      <Route path="notifications" element={L(<Notifications />)} />
                      <Route path="profile" element={L(<Profile />)} />
                      <Route path="about" element={L(<About />)} />
                      <Route path="contact" element={L(<Contact />)} />
                      <Route path="terms" element={L(<Terms />)} />
                      <Route path="privacy" element={L(<Privacy />)} />
                      <Route path="returns" element={L(<ReturnPolicy />)} />
                      <Route path="shipping" element={L(<Shipping />)} />
                      <Route
                        path="orders/:id"
                        element={<ProtectedRoute>{L(<OrderTracking />)}</ProtectedRoute>}
                      />
                      <Route
                        path="order-success/:id"
                        element={L(<OrderSuccess />)}
                      />
                      <Route
                        path="checkout"
                        element={L(<Checkout />)}
                      />
                      <Route
                        path="track-order"
                        element={L(<GuestTrackOrder />)}
                      />
                      <Route path="*" element={<NotFound />} />
                    </Route>
                  </Routes>
                  <Suspense fallback={null}>
                    <QuickBuySheet />
                    <AgeGateModal />
                  </Suspense>
                  <CookieConsentBanner />
                  <Toaster position="top-center" />
                </QuickBuyProvider>
              </NotificationProvider>
            </ShopProvider>
          </AuthProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </div>
  );
}

export default App;
